// A person-representation row used everywhere someone (or a group) appears
// across ticket 302: Friends, Groups (group-as-card), GroupDetail members,
// AddFriendSheet results, the invite pickers and SessionDetail's invited/
// presence lists. One component instead of six divergent "card row"
// compositions -- see design.md's Checkpoint 5. The row carries a single
// aria-label combining name + secondary line, so a screen reader announces
// one coherent row rather than image-then-text-then-badge separately; the
// secondary line is plain text the caller already wrote (role/status/
// presence), never colour alone.
import { Avatar, Body, Caption, Fill, Row } from "../ui";
import type { AvatarPresence, AvatarSize } from "../ui";

export interface UserCardProps {
  /** Person or group name. */
  title: string;
  /** Role, status, presence or member-count -- text, never colour alone. */
  subtitle?: string | undefined;
  /** A second secondary line (GroupDirectory's group description) -- a data addition to the
   * existing row for the one call site that needs it, not a new component (design.md's
   * Checkpoint 5 rework-pass-2 note: "a data field added to UserCard's existing secondary-line
   * slot, not a new variant"). */
  description?: string | undefined;
  initials: string;
  avatarUrl?: string | undefined;
  avatarSize?: AvatarSize;
  presence?: AvatarPresence | undefined;
  /** A Button/Pill/checkbox -- the row's one optional action slot. */
  trailing?: React.ReactNode;
  testId?: string;
}

export function UserCard({
  title,
  subtitle,
  description,
  initials,
  avatarUrl,
  avatarSize = "row",
  presence,
  trailing,
  testId,
}: UserCardProps): React.JSX.Element {
  // One coherent announcement (name, then secondary line, then description) rather than
  // image-then-text-then-badge separately (design.md's UserCard/Avatar a11y note).
  const label = [title, subtitle, description]
    .filter((part) => part !== undefined && part !== "")
    .join(", ");
  return (
    <Row gap="3" paddingVertical="3" role="group" aria-label={label} data-testid={testId}>
      <Avatar initials={initials} src={avatarUrl} size={avatarSize} presence={presence} />
      <Fill gap={1}>
        <Body weight="medium" truncate>
          {title}
        </Body>
        {subtitle !== undefined && subtitle !== "" && <Caption truncate>{subtitle}</Caption>}
        {description !== undefined && description !== "" && (
          <Caption truncate>{description}</Caption>
        )}
      </Fill>
      {trailing !== undefined && (
        <Row gap="2" flexShrink={0}>
          {trailing}
        </Row>
      )}
    </Row>
  );
}
